import { Component, DestroyRef, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { convertEpochToDateString } from '@functions/functions';
import { GlobalFunctions, imagePath, imgTypes, TriggerEventTypeMap } from '@global/GlobalFunctions';
import { IAccount } from '@services/accounts.service';
import { Action } from '@services/actions-data.service';
import { LevelAsset } from '@services/assets-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { EventComment, RuleEvent, TimelineItem } from '@services/events-data.service';
import { Rule, RuleEdit } from '@services/rules-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { TimelineItemModel } from '@syncfusion/ej2-angular-layouts';
import { combineLatest, distinctUntilChanged, filter, firstValueFrom, Subject, take } from 'rxjs';
import { Location } from '@angular/common';
import { CmmsService, ICaseFile, iCauseTypes, iTriggerTypes, IWorkEvent } from '@services/cmms.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'app-sap-case-detail',
	templateUrl: './sap-case-detail.component.html',
	styleUrl: './sap-case-detail.component.scss'
})
export class SAPCaseDetailComponent {

	/** triggerTypes - values correspond to value in db table */
	public triggerTypes: iTriggerTypes[] = [];

	/** triggerTypes - values correspond to value in db table */
	public causeTypes: iCauseTypes[] = [];

	/** Work Events */
	public workEvents: IWorkEvent[] = [];

	/** Path to Assets Images */
	public imagePathAssets(): string {
		return imagePath(this.accountGUID, imgTypes.organisationassets).toLowerCase()
	}

	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);

	/** accountGUID */
	public accountGUID: string;

	/** organisationUID */
	public organisationUID: string;

	/** asset */
	public asset: LevelAsset;

	/** thisUser */
	public thisUser: WebAppUser;

	/** selectedAccount */
	public selectedAccount: IAccount;

	/** ruleEventID */
	public ruleEventID: number = 0;

	/** Debugging */
	public displayTimeline: boolean = true;

	/** timelineItems */
	public timelineItems: TimelineItemModel[] = [];

	/** Timeline */
	public Timeline: TimelineItem[] = [];

	/** Whether to automatically open the comments accordion */
	public expanded: boolean = false;

	/** now */
	public now: number = new Date().getTime();

	/** cases */
	public cmmsCase: ICaseFile = {} as ICaseFile;

	/** dotCssIcon */
	public dotCssIcon: string[] = ["", "add_task", "thumb_up", "work_outline", "lock"];

	/** Case ID */
	public caseID: string = "";

	/**
	   * Default Constructor
	   * @param dialog dialog
	   * @param authenticationService authenticationService
	   * @param siteSubscriptions siteSubscriptions
	   * @param router router
	   * @param route ActivatedRoute
	   * @param globalFunctions globalFunctions
	   * @param location Location
	   * @param cmmsService CmmsService
	   */
	constructor(
		public dialog: MatDialog,
		public authenticationService: AuthenticationService,
		public siteSubscriptions: siteSubscriptions,
		private router: Router,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions,
		private location: Location,
		private cmmsService: CmmsService
	) {
		combineLatest([
			this.authenticationService.currentUser$,
			this.route.paramMap,
			this.route.queryParamMap
		]).pipe(
			takeUntilDestroyed(this.destroyRef), distinctUntilChanged(),
			// Ensure that subscriptions are not null or undefined
			filter(([currentUser, paramMap, queryParamMap]) =>
				!!currentUser && !!paramMap && !!queryParamMap)
		).subscribe({
			next: async ([currentUser, paramMap, queryParamMap]) => {

				let [selectedAccount, selectedAccountGUID, selectedOrganisation, selectedOrganisationUID] = await firstValueFrom(
					combineLatest([
						this.siteSubscriptions.selectedAccount$.pipe(take(1)),
						this.siteSubscriptions.selectedAccountGUID$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisation$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisationUID$.pipe(take(1))
					]
					)
				);

				this.selectedAccount = selectedAccount;
				this.accountGUID = selectedAccountGUID;
				this.organisationUID = selectedOrganisationUID;

				let urlAccountID = this.router.url.split('/')[1];
				this.caseID = paramMap.get('caseID') ?? "";

				if (this.accountGUID.toLowerCase() != urlAccountID.toLowerCase() && this.router.url.includes('actions') && !this.router.url.includes('routing#')) {
					this.router.navigateByUrl(`routing#${this.router.url}`);
				}

				this.thisUser = currentUser;

				let expanded = queryParamMap.get('expanded');
				if (expanded == "true") {
					this.expanded = true;
				}

				this.getCases(this.caseID);
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * getCases
	 * @param caseID 
	 */
	getCases(caseID?: string) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		combineLatest([
			this.cmmsService.getCases$(this.accountGUID, this.organisationUID, caseID),
			this.cmmsService.getWorkEvents$(this.accountGUID, this.organisationUID, undefined, caseID),
			this.cmmsService.getCauseTypes$(),
			this.cmmsService.getTriggerTypes$()
		]).pipe(
			takeUntilDestroyed(this.destroyRef), distinctUntilChanged(),
			// Ensure that subscriptions are not null or undefined
			filter(([cmmsCases, workEvents, causeTypes, triggerTypes]) =>
				!!cmmsCases && !!workEvents && !!causeTypes && !!triggerTypes)
		).subscribe({
			next: ([cmmsCases, workEvents, causeTypes, triggerTypes]) => {
				this.cmmsCase = cmmsCases.find((x) => x.Id == caseID) ?? {} as ICaseFile;
				this.workEvents = workEvents;
				this.causeTypes = causeTypes;
				this.triggerTypes = triggerTypes;

				this.asset = this.cmmsCase.Asset ? this.cmmsCase.Asset : {} as LevelAsset;

				this.BuildTimeline(this.cmmsCase, this.workEvents);
			},
			error: (error) => {
				if (error.status != 404) { this.globalFunctions.HandleError(error); }
			}
		});
	}

	/**
	 * BuildTimeline
	 * @param cmmsCase 
	 * @param workEvents 
	 */
	BuildTimeline(cmmsCase: ICaseFile, workEvents: IWorkEvent[]) {

		this.timelineItems = [
			{
				dotCss: "1",
				content: JSON.stringify(cmmsCase),
				oppositeContent: this.convertEpoch(cmmsCase.OpenedAt)
			},
			...(cmmsCase.InCMMS
				? [
					{
						dotCss: "2",
						content: JSON.stringify(cmmsCase),
						oppositeContent: this.convertEpoch(cmmsCase.CMMSCreatedAt)
					}
				]
				: []),
			...workEvents.map((event: IWorkEvent) => ({
				dotCss: "3",
				content: JSON.stringify(event),
				oppositeContent: this.convertEpoch(event.CreationTime),
			})),
			...(!cmmsCase.IsOpen
				? [
					{
						dotCss: "4",
						content: JSON.stringify(cmmsCase),
						oppositeContent: this.convertEpoch(cmmsCase.ClosedAt)
					}
				]
				: [])
		];

	}

	/**
	 * View the Image Fullsize
	 * @param imageURL
	 */
	viewImage(imageURL: string) {
		this.globalFunctions.viewImageFullSize(imageURL);
	}

	/** back */
	back() {
		this.location.back();
	}

	/**
	 * goToAsset
	 */
	goToAsset() {
		this.siteSubscriptions.SetSelectedAsset(this.asset);
		this.router.navigate([`./${this.accountGUID}/organisation/${this.organisationUID}/location/${this.asset.ParentUID}/asset/${this.asset.AssetID}`]);
	}

	/**
	 * OpenTrigger
	 * @param caseFile
	 */
	OpenTrigger(caseFile: ICaseFile) {

		let eventType = TriggerEventTypeMap[caseFile.TriggerType] ?? null
		if (eventType) {
			const queryParams = {
				eventType: eventType ?? null
			};

			this.router.navigate([
				this.route.snapshot.paramMap.get('account'),
				'organisation',
				this.route.snapshot.paramMap.get('organisation'),
				'event',
				caseFile.TriggerID
			], { queryParams: queryParams, queryParamsHandling: 'merge' });
		}
	}

	/**
	 * Converts the HH:MM format required for the trigger time to an int of
	 * total minutes
	 * @param timeStr timeStr
	 * @returns total minutes
	 */
	timeToMinutes(timeStr: string): number {
		const [hours, minutes] = timeStr.split(':').map(Number);
		const totalMinutes = hours * 60 + minutes;
		return totalMinutes;
	}

	/**
	 * Converts int of total minutes to [HH,MM] string array
	 * @param totalMinutes totalMinutes
	 * @returns HH:MM string
	 */
	convertMinutesToHHMM(totalMinutes: number): string[] {
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		const hoursString = hours.toString().padStart(2, '0');
		const minutesString = minutes.toString().padStart(2, '0');
		return [hoursString, minutesString]
	}

	/**
	 * checkNotLast
	 * @param data data
	 * @returns
	 */
	checkNotLast(data: any): boolean {
		let result: boolean = false;
		let finalItem: number = this.timelineItems.length - 1;
		if (data.itemIndex == finalItem) {
			result = true;
		}
		return result;
	}

	/**
	 * buildObject
	 * @param content 
	 * @param _category 
	 * @returns 
	 */
	buildObject(content: string, _category: string): any {

		let category: number = parseInt(_category);
		if (category == 1 || 2 || 4) {
			let result = JSON.parse(content);
			return result;
		} else if (category == 3) {
			let result = JSON.parse(content);
			return result;
		}
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpochString(_epoch: string): string {
		/** Use the Global Function */
		let epoch: number = parseInt(_epoch);
		return convertEpochToDateString(epoch);
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * goToTimelineItem
	 * @param timelineItem timelineItem
	 */
	goToTimelineItem(timelineItem: any) {
		let category: number = parseInt(timelineItem.dotCss);
		if (category == 1 || category == 2 || category == 3) {
			let result = <RuleEvent[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountGUID}/organisation/${this.organisationUID}/event/${result[0].RuleEventID}`],
				{ queryParams: { eventType: 2 } }
			);
		} else if (category == 4) {
			let result = <EventComment[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountGUID}/organisation/${this.organisationUID}/event/${result[0].EventID}`],
				{ queryParams: { eventType: 2, expanded: true } }
			);
		} else if (category == 6) {
			let result = <EventComment[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountGUID}/organisation/${this.organisationUID}/actions/${result[0].EventID}`],
				{ queryParams: { expanded: true } }
			);
		} else if (category == 5 || category == 7) {
			let result = <Action[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountGUID}/organisation/${this.organisationUID}/actions/${result[0].ActionID}`]
			);
		} else if (category == 8 || category == 9) {
			let result = <Rule[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountGUID}/organisation/${this.organisationUID}/rules/${result[0].RuleID}`]
			);
		} else if (category == 10) {
			let result = <RuleEdit[]>JSON.parse(timelineItem.content);
			this.router.navigate(
				[`/${this.accountGUID}/organisation/${this.organisationUID}/rules/${result[0].RuleID}`]
			);
		}
	}

	/**
	 * getTriggerTypeFromValue
	 * @param value value
	 * @returns
	 */
	getTriggerTypeFromValue(value: number): string {
		let result = this.triggerTypes.find((x: iTriggerTypes) => x.TriggerType == value)?.Description;
		return result ? result : "";
	}

	/**
	 * getCauseTypeFromValue
	 * @param value value
	 * @returns
	 */
	getCauseTypeFromValue(value: number): string {
		let result = this.causeTypes.find((x: iCauseTypes) => x.CauseType == value)?.Description;
		return result ? result : "";
	}
}
